import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Download,
  Inbox,
  UserPlus,
  CheckCircle2,
  Trophy,
  Mail,
  StickyNote,
  Search,
  X,
  Circle,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};
const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'lost'];

const STATUS_STYLES = {
  new:       { fg: 'var(--danger)', bg: 'var(--danger-10)', label: 'New' },
  contacted: { fg: 'var(--warn)',   bg: 'var(--warn-10)',   label: 'Contacted' },
  qualified: { fg: 'var(--info)',   bg: 'var(--info-10)',   label: 'Qualified' },
  converted: { fg: 'var(--ok)',     bg: 'var(--ok-10)',     label: 'Converted' },
  lost:      { fg: 'var(--ts)',     bg: 'rgba(240,237,248,0.06)', label: 'Lost' },
};

// ─── helpers ────────────────────────────────────────────────────────
function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `Today, ${hh}:${mm}`;
  }
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays >= 1 && diffDays <= 30) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-GB');
}

function toCSV(rows) {
  const headers = ['Name', 'Email', 'Interested In', 'Goal', 'Source', 'Status', 'Created At'];
  const esc = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [r.name, r.email, r.interested_in, r.goal, r.source, r.status, r.created_at].map(esc).join(','),
  );
  return [headers.join(','), ...body].join('\n');
}

function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ─── Badge ─────────────────────────────────────────────────────────
function StatusBadge({ status, onClick, disabled }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.new;
  const Component = onClick ? 'button' : 'span';
  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      disabled={disabled}
      title={onClick ? 'Click to cycle status' : undefined}
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded transition-opacity hover:opacity-80"
      style={{ background: s.bg, color: s.fg, opacity: disabled ? 0.5 : 1 }}
    >
      <Circle size={8} fill={s.fg} style={{ color: s.fg }} />
      {s.label}
    </Component>
  );
}

// ─── Stat card ─────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, testId, tone }) {
  const toneColor = tone ? { color: `var(--${tone})` } : {};
  return (
    <div data-testid={testId} className="p-5 rounded-lg" style={CARD_STYLE}>
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {label}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{ width: 28, height: 28, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: 'var(--white)', ...toneColor }}>
        {new Intl.NumberFormat('en-GB').format(value)}
      </p>
    </div>
  );
}

// ─── Reply modal ───────────────────────────────────────────────────
function ReplyModal({ lead, onCancel, onSend, sending }) {
  const [subject, setSubject] = useState(`Re: your enquiry about ${lead.interested_in}`);
  const [body, setBody] = useState(
    `Hi ${lead.name.split(' ')[0]},\n\n` +
      `Thanks for your interest in ${lead.interested_in} — happy to help you explore "${lead.goal}".\n\n` +
      `Do you have 20 minutes this week for a call? I'll tailor the next steps to your goal and walk you through the intake timeline.\n\n` +
      `Best,\nAcodia Team`,
  );
  const [err, setErr] = useState(null);
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onCancel]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await onSend({ subject, body });
    } catch (ex) {
      setErr(ex.message || 'Send failed');
    }
  };

  return (
    <div
      data-testid="admin-lead-reply-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,5,10,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg p-6"
        style={CARD_STYLE}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>new · reply</p>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--white)' }}>Reply to {lead.name}</h2>
          </div>
          <button type="button" onClick={onCancel} className="p-1" style={{ color: 'var(--ts)' }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {err && (
          <div className="p-2 rounded mb-3" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
            <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{err}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>To</label>
            <Input value={lead.email} disabled className="font-mono text-sm h-10 rounded-[3px]" style={INPUT_STYLE} />
          </div>
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Subject</label>
            <Input
              data-testid="admin-lead-reply-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="font-body text-sm h-10 rounded-[3px]"
              style={INPUT_STYLE}
              required
            />
          </div>
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Body</label>
            <textarea
              data-testid="admin-lead-reply-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              maxLength={5000}
              className="w-full font-body text-sm rounded-[3px] p-2 resize-none"
              style={INPUT_STYLE}
              required
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onCancel} className="font-body text-xs px-3 py-2 rounded-[3px]" style={{ color: 'var(--tm)', border: '1px solid var(--b1)' }}>
            Cancel
          </button>
          <Button
            type="submit"
            data-testid="admin-lead-reply-send"
            disabled={sending}
            className="btn-shimmer font-body text-xs px-4 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: sending ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : 'Send Reply'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Notes modal ──────────────────────────────────────────────────
function NotesModal({ lead, onCancel, onAdd, adding }) {
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onCancel]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!text.trim()) { setErr('Note is empty'); return; }
    try {
      await onAdd(text);
      setText('');
    } catch (ex) {
      setErr(ex.message || 'Failed to add note');
    }
  };

  const notes = [...(lead.notes || [])].reverse();

  return (
    <div
      data-testid="admin-lead-notes-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,5,10,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg p-6"
        style={CARD_STYLE}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>notes · {notes.length}</p>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--white)' }}>{lead.name}</h2>
          </div>
          <button type="button" onClick={onCancel} className="p-1" style={{ color: 'var(--ts)' }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto mb-4 pr-1">
          {notes.length === 0 ? (
            <p className="font-body text-xs" style={{ color: 'var(--te)' }}>No notes yet.</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="py-2" style={{ borderBottom: '1px solid var(--b1)' }}>
                <p className="font-body text-sm" style={{ color: 'var(--white)' }}>{n.text}</p>
                <p className="font-mono text-[10px] mt-0.5" style={{ color: 'var(--te)' }}>
                  {n.author_email} · {formatRelative(n.created_at)}
                </p>
              </div>
            ))
          )}
        </div>

        {err && (
          <div className="p-2 rounded mb-3" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
            <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{err}</p>
          </div>
        )}

        <form onSubmit={submit}>
          <textarea
            data-testid="admin-lead-notes-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add a new note…"
            className="w-full font-body text-sm rounded-[3px] p-2 resize-none mb-3"
            style={INPUT_STYLE}
          />
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onCancel} className="font-body text-xs px-3 py-2 rounded-[3px]" style={{ color: 'var(--tm)', border: '1px solid var(--b1)' }}>
              Close
            </button>
            <Button
              type="submit"
              data-testid="admin-lead-notes-save"
              disabled={adding}
              className="btn-shimmer font-body text-xs px-4 py-2 rounded-[3px]"
              style={{ background: 'var(--a)', color: 'var(--white)', opacity: adding ? 0.6 : 1 }}
            >
              {adding ? 'Adding…' : 'Add Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────
export default function LeadsEnquiriesSection({ onDataChanged }) {
  const { adminFetch } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cyclingId, setCyclingId] = useState(null);
  const [replyLead, setReplyLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      const res = await adminFetch(`${BACKEND_URL}/api/admin/leads?${params.toString()}`);
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const body = await res.json();
      setLeads(body.leads);
      setSummary(body.summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, statusFilter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleCycleStatus = async (lead) => {
    setCyclingId(lead.id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/leads/${lead.id}/status-cycle`, { method: 'POST' });
      if (!res.ok) throw new Error(`Status cycle failed: ${res.status}`);
      const body = await res.json();
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? body.lead : l)));
      setSummary(body.summary);
      onDataChanged?.();
    } catch (err) {
      toast.error('Status change failed', { description: err.message });
    } finally {
      setCyclingId(null);
    }
  };

  const handleSendReply = async ({ subject, body }) => {
    setSending(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/leads/${replyLead.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Reply failed: ${res.status}`);
      }
      toast.success('Reply drafted', { description: `To: ${replyLead.email}` });
      setReplyLead(null);
    } finally {
      setSending(false);
    }
  };

  const handleAddNote = async (text) => {
    setAdding(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/leads/${notesLead.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Note failed: ${res.status}`);
      }
      const { lead: fresh } = await res.json();
      setLeads((prev) => prev.map((l) => (l.id === fresh.id ? fresh : l)));
      setNotesLead(fresh);
      toast.success('Note added');
    } finally {
      setAdding(false);
    }
  };

  const handleExport = async () => {
    try {
      await adminFetch(`${BACKEND_URL}/api/admin/leads/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: leads.length, status: statusFilter }),
      });
      const csv = toCSV(leads);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(csv, `acodia-leads-${stamp}.csv`, 'text/csv;charset=utf-8');
      toast.success('Exported to CSV', { description: `${leads.length} rows downloaded.` });
    } catch (err) {
      toast.error('Export failed', { description: err.message });
    }
  };

  const showNoResults = !loading && leads.length === 0;

  return (
    <div className="space-y-5" data-testid="admin-leads-enquiries-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · leads-enquiries
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
            Leads &amp; Enquiries
          </h2>
        </div>
        <Button
          data-testid="admin-leads-export"
          onClick={handleExport}
          className="font-body text-sm h-10 px-4 rounded-[3px]"
          style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
        >
          <Download size={14} className="mr-1.5" />
          Export CSV
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-leads-summary">
        <StatCard label="Total Leads"       value={summary.total}     icon={Inbox}         testId="admin-leads-stat-total" />
        <StatCard label="New (uncontacted)" value={summary.new}       icon={UserPlus}      testId="admin-leads-stat-new"       tone="danger" />
        <StatCard label="Qualified"         value={summary.qualified} icon={CheckCircle2}  testId="admin-leads-stat-qualified" tone="info" />
        <StatCard label="Converted"         value={summary.converted} icon={Trophy}        testId="admin-leads-stat-converted" tone="ok" />
      </div>

      {/* Filters */}
      <div className="p-4 rounded-lg flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" style={CARD_STYLE}>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>status</span>
          <select
            data-testid="admin-leads-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="font-body text-sm h-9 rounded-[3px] px-2"
            style={INPUT_STYLE}
          >
            <option value="all" style={{ background: 'var(--l1)' }}>All Status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} style={{ background: 'var(--l1)' }}>
                {STATUS_STYLES[s].label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
          <Input
            data-testid="admin-leads-filter-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="font-body text-sm h-9 rounded-[3px] pl-9"
            style={INPUT_STYLE}
          />
        </div>
        <span className="font-mono text-[10px]" style={{ color: 'var(--te)' }}>
          {leads.length} of {summary.total}
        </span>
      </div>

      {/* Leads table */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-leads-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'var(--l2)', borderBottom: '1px solid var(--b1)' }}>
                <Th>Name</Th>
                <Th>Interested In</Th>
                <Th>Goal</Th>
                <Th>Source</Th>
                <Th>Date</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center font-mono text-xs" style={{ color: 'var(--a)' }}>Loading…</td></tr>
              )}
              {showNoResults && (
                <tr><td colSpan={7} className="p-6 text-center font-body text-sm" style={{ color: 'var(--te)' }}>
                  No leads match these filters.
                </td></tr>
              )}
              {leads.map((l, idx) => (
                <tr
                  key={l.id}
                  data-testid={`admin-lead-row-${l.id}`}
                  style={{ borderBottom: idx === leads.length - 1 ? 'none' : '1px solid var(--b1)' }}
                >
                  <Td>
                    <p className="font-body text-sm font-medium" style={{ color: 'var(--white)' }}>{l.name}</p>
                    <p className="font-mono text-[11px] mt-0.5" style={{ color: 'var(--ts)' }}>{l.email}</p>
                  </Td>
                  <Td>
                    <span className="font-body text-sm" style={{ color: 'var(--tm)' }}>{l.interested_in}</span>
                  </Td>
                  <Td>
                    <span className="font-body text-sm" style={{ color: 'var(--tm)' }}>{l.goal}</span>
                  </Td>
                  <Td>
                    <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>{l.source}</span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>{formatRelative(l.created_at)}</span>
                  </Td>
                  <Td>
                    <StatusBadge
                      status={l.status}
                      onClick={() => handleCycleStatus(l)}
                      disabled={cyclingId === l.id}
                    />
                  </Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-1.5">
                      <IconPill testId={`admin-lead-reply-${l.id}`} icon={Mail} label="Reply" onClick={() => setReplyLead(l)} />
                      <IconPill testId={`admin-lead-notes-${l.id}`} icon={StickyNote} label={`Notes${(l.notes || []).length ? ` (${l.notes.length})` : ''}`} onClick={() => setNotesLead(l)} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {replyLead && (
        <ReplyModal lead={replyLead} sending={sending} onCancel={() => setReplyLead(null)} onSend={handleSendReply} />
      )}
      {notesLead && (
        <NotesModal lead={notesLead} adding={adding} onCancel={() => setNotesLead(null)} onAdd={handleAddNote} />
      )}
    </div>
  );
}

// ─── Table helpers ────────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3"
      style={{ color: 'var(--ts)', textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td className="px-4 py-3" style={{ textAlign: align, verticalAlign: 'top' }}>{children}</td>;
}
function IconPill({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="inline-flex items-center gap-1 font-body text-xs px-2 py-1 rounded-[3px] transition-colors"
      style={{ color: 'var(--tm)', border: '1px solid var(--b1)', background: 'transparent' }}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}
